// src/components/IONMReview.jsx
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  FormControl,
  FormLabel,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  Link,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Icon,
  Tooltip,
  InputGroup,
  Input,
  InputRightElement,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  Divider,
  List,
  ListItem,
  ListIcon,
  Code,
    SimpleGrid,
} from '@chakra-ui/react';
import { SearchIcon, WarningIcon, CheckCircleIcon, TimeIcon, InfoIcon } from '@chakra-ui/icons';
import { FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';

const IONMReview = () => {
  const { accessToken } = useContext(AppContext);
  const [daysBack, setDaysBack] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [casesNeedingReview, setCasesNeedingReview] = useState([]);
  const [casesWithoutAnalysis, setCasesWithoutAnalysis] = useState([]);
  const [totalCasesNeedingReview, setTotalCasesNeedingReview] = useState(0);
  const [totalCasesWithoutAnalysis, setTotalCasesWithoutAnalysis] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const toast = useToast();
  
  // State for the quick review modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [isLoadingCaseDetails, setIsLoadingCaseDetails] = useState(false);
  const [caseDetails, setCaseDetails] = useState(null);

  // Colors for better visual hierarchy
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const highlightColor = useColorModeValue('blue.50', 'blue.900');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');

  // Fetch cases needing review
  const fetchCasesNeedingReview = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await callApi(`/ionm/cases_needing_review?days_back=${daysBack}`, accessToken);
      const data = await response.json();
      
      if (data.status === 'success') {
        setCasesNeedingReview(data.cases);
        setTotalCasesNeedingReview(data.total_count);
      } else {
        setError(data.message || 'Failed to fetch cases needing review');
      }
    } catch (error) {
      console.error('Error fetching cases needing review:', error);
      setError('Failed to fetch cases needing review. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch cases without analysis
  const fetchCasesWithoutAnalysis = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await callApi(`/ionm/cases_without_analysis?days_back=${daysBack}`, accessToken);
      const data = await response.json();
      
      if (data.status === 'success') {
        setCasesWithoutAnalysis(data.cases);
        setTotalCasesWithoutAnalysis(data.total_count);
      } else {
        setError(data.message || 'Failed to fetch cases without analysis');
      }
    } catch (error) {
      console.error('Error fetching cases without analysis:', error);
      setError('Failed to fetch cases without analysis. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch full case details for the modal
  const fetchCaseDetails = async (caseId) => {
    setIsLoadingCaseDetails(true);
    
    try {
      const response = await callApi(`/ionm/analysis_results/${caseId}`, accessToken);
      const data = await response.json();
      
      if (data.status === 'success') {
        setCaseDetails(data);
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to fetch case details",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching case details:', error);
      toast({
        title: "Error",
        description: "Failed to fetch case details. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCaseDetails(false);
    }
  };

  // Open the quick review modal
  const openQuickReview = (case_) => {
    setSelectedCase(case_);
    setIsModalOpen(true);
    fetchCaseDetails(case_.case_id);
  };

  // Run analysis on a case
  const runAnalysisOnCase = async (caseId) => {
    try {
      toast({
        title: "Starting analysis...",
        description: `Analyzing case ${caseId}`,
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      
      const response = await callApi('/ionm/auto_analyze_case', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: caseId }),
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        toast({
          title: "Analysis complete",
          description: `Case ${caseId} analyzed with score: ${data.necessity_score}`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        
        // Refresh the lists
        fetchCasesWithoutAnalysis();
        fetchCasesNeedingReview();
      } else {
        toast({
          title: "Analysis failed",
          description: data.message || "An error occurred during analysis",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      toast({
        title: "Analysis failed",
        description: "An unexpected error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchCasesNeedingReview();
    fetchCasesWithoutAnalysis();
  }, [daysBack]);

  // Filter cases based on search term
  const filteredCasesNeedingReview = casesNeedingReview.filter(
    case_ => case_.case_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
             (case_.primary_diagnosis && case_.primary_diagnosis.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredCasesWithoutAnalysis = casesWithoutAnalysis.filter(
    case_ => case_.case_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
             (case_.patient_id && case_.patient_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get score color based on value
  const getScoreColor = (score) => {
    if (score >= 70) return "green";
    if (score >= 50) return "yellow";
    return "red";
  };

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <Flex justify="space-between" align="center" mb={6}>
          <Heading as="h1">IONM Cases Review Dashboard</Heading>
          <HStack spacing={4}>
            <FormControl w="200px">
              <FormLabel fontSize="sm">Time Period</FormLabel>
              <Select
                value={daysBack}
                onChange={(e) => setDaysBack(Number(e.target.value))}
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </Select>
            </FormControl>
            <Button 
              colorScheme="blue" 
              onClick={() => {
                fetchCasesNeedingReview();
                fetchCasesWithoutAnalysis();
              }}
              isLoading={isLoading}
            >
              Refresh
            </Button>
          </HStack>
        </Flex>

        {/* Dashboard Stats */}
        <Flex mb={6} gap={4}>
          <Card flex="1" variant="outline" bg={cardBg}>
            <CardBody>
              <StatGroup>
                <Stat>
                  <StatLabel>Cases Needing Review</StatLabel>
                  <StatNumber color="red.500">
                    <Flex align="center">
                      <Icon as={FaExclamationTriangle} mr={2} />
                      {totalCasesNeedingReview}
                    </Flex>
                  </StatNumber>
                </Stat>
              </StatGroup>
            </CardBody>
          </Card>
          
          <Card flex="1" variant="outline" bg={cardBg}>
            <CardBody>
              <StatGroup>
                <Stat>
                  <StatLabel>Cases Without Analysis</StatLabel>
                  <StatNumber color="orange.500">
                    <Flex align="center">
                      <Icon as={TimeIcon} mr={2} />
                      {totalCasesWithoutAnalysis}
                    </Flex>
                  </StatNumber>
                </Stat>
              </StatGroup>
            </CardBody>
          </Card>
          
          <Card flex="1" variant="outline" bg={cardBg}>
            <CardBody>
              <StatGroup>
                <Stat>
                  <StatLabel>Total Cases</StatLabel>
                  <StatNumber>
                    {totalCasesNeedingReview + totalCasesWithoutAnalysis}
                  </StatNumber>
                </Stat>
              </StatGroup>
            </CardBody>
          </Card>
        </Flex>

        {/* Search Bar */}
        <InputGroup mb={6} maxW="500px">
          <Input
            placeholder="Search by case ID or diagnosis code"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <InputRightElement>
            <SearchIcon color="gray.500" />
          </InputRightElement>
        </InputGroup>

        {error && (
          <Alert status="error" mb={6}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {/* Tabs for different case lists */}
        <Tabs 
          variant="enclosed" 
          colorScheme="blue" 
          index={activeTab}
          onChange={(index) => setActiveTab(index)}
        >
          <TabList>
            <Tab>Cases Needing Review ({filteredCasesNeedingReview.length})</Tab>
            <Tab>Cases Without Analysis ({filteredCasesWithoutAnalysis.length})</Tab>
          </TabList>

          <TabPanels>
            {/* Cases Needing Review Tab */}
            <TabPanel px={0}>
              {isLoading && activeTab === 0 ? (
                <Flex justify="center" py={10}>
                  <Spinner size="xl" />
                </Flex>
              ) : filteredCasesNeedingReview.length > 0 ? (
                <Box overflowX="auto">
                  <Table variant="simple" size="md">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th>Case ID</Th>
                        <Th>Primary Diagnosis</Th>
                        <Th>Necessity Score</Th>
                        <Th>Date Analyzed</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredCasesNeedingReview.map((case_) => (
                        <Tr key={case_.results_id}>
                          <Td fontWeight="medium">{case_.case_id}</Td>
                          <Td>{case_.primary_diagnosis || "Not specified"}</Td>
                          <Td>
                            <Badge 
                              colorScheme={getScoreColor(case_.necessity_score)}
                              fontSize="md"
                              px={2}
                              py={1}
                              borderRadius="md"
                            >
                              {case_.necessity_score}
                            </Badge>
                          </Td>
                          <Td>{formatDate(case_.created_at)}</Td>
                          <Td>
                            <HStack spacing={2}>
                              <Button 
                                size="sm" 
                                colorScheme="teal"
                                leftIcon={<InfoIcon />}
                                onClick={() => openQuickReview(case_)}
                              >
                                Quick View
                              </Button>
                              <Button 
                                as={RouterLink}
                                to={`/ionm-coding-optimizer?case_id=${case_.case_id}`}
                                size="sm" 
                                colorScheme="blue"
                              >
                                Review
                              </Button>
                              <Button 
                                size="sm" 
                                colorScheme="green"
                                onClick={() => runAnalysisOnCase(case_.case_id)}
                              >
                                Re-analyze
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  No cases needing review were found for the selected time period.
                </Alert>
              )}
            </TabPanel>

            {/* Cases Without Analysis Tab */}
            <TabPanel px={0}>
              {isLoading && activeTab === 1 ? (
                <Flex justify="center" py={10}>
                  <Spinner size="xl" />
                </Flex>
              ) : filteredCasesWithoutAnalysis.length > 0 ? (
                <Box overflowX="auto">
                  <Table variant="simple" size="md">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th>Case ID</Th>
                        <Th>Patient ID</Th>
                        <Th>Procedure Date</Th>
                        <Th>Created Date</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredCasesWithoutAnalysis.map((case_) => (
                        <Tr key={case_.case_id}>
                          <Td fontWeight="medium">{case_.case_id}</Td>
                          <Td>{case_.patient_id || "Not specified"}</Td>
                          <Td>{case_.procedure_date ? formatDate(case_.procedure_date) : "Not specified"}</Td>
                          <Td>{formatDate(case_.created_at)}</Td>
                          <Td>
                            <HStack spacing={2}>
                              <Button 
                                size="sm" 
                                colorScheme="green"
                                onClick={() => runAnalysisOnCase(case_.case_id)}
                              >
                                Analyze
                              </Button>
                              <Button 
                                as={RouterLink}
                                to={`/ionm-coding-optimizer?case_id=${case_.case_id}`}
                                size="sm" 
                                colorScheme="blue"
                              >
                                Manual Review
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  No cases without analysis were found for the selected time period.
                </Alert>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>

      // Quick Review Modal
<Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl" scrollBehavior="inside">
  <ModalOverlay />
  <ModalContent>
    <ModalHeader>
      Case Review: {selectedCase?.case_id}
      <Badge 
        ml={2} 
        colorScheme={selectedCase ? getScoreColor(selectedCase.necessity_score) : "gray"}
        fontSize="md"
      >
        Score: {selectedCase?.necessity_score}
      </Badge>
    </ModalHeader>
    <ModalCloseButton />
    <ModalBody>
      {isLoadingCaseDetails ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" />
        </Flex>
      ) : caseDetails ? (
        <VStack align="stretch" spacing={4}>
          {/* Primary Diagnosis */}
          <Box>
            <Heading size="md" mb={2}>Primary Diagnosis</Heading>
            <Text fontWeight="bold">{caseDetails.primary_diagnosis || "Not specified"}</Text>
            {caseDetails.full_results?.recommendations?.primary_diagnosis && (
              <Text>
                {caseDetails.full_results.recommendations.primary_diagnosis.description} 
                (Confidence: {caseDetails.full_results.recommendations.primary_diagnosis.confidence}%)
              </Text>
            )}
          </Box>
          
          <Divider />
          {/* Payer Information */}
<Box>
  <Heading size="md" mb={2}>Payer Information</Heading>
  <HStack spacing={4}>
    <Box>
      <Text fontWeight="bold">Payer:</Text>
      <Text>{caseDetails.full_results?.payer_used?.toUpperCase() || "Not specified"}</Text>
    </Box>
    {caseDetails.full_results?.carrier_name_used && (
      <Box>
        <Text fontWeight="bold">Carrier:</Text>
        <Text>{caseDetails.full_results.carrier_name_used}</Text>
      </Box>
    )}
    {caseDetails.full_results?.state_used && (
      <Box>
        <Text fontWeight="bold">State:</Text>
        <Text>{caseDetails.full_results.state_used}</Text>
      </Box>
    )}
    <Box>
      <Text fontWeight="bold">Override State:</Text>
      <Text>{caseDetails.full_results?.override_state_used ? "Yes" : "No"}</Text>
    </Box>
  </HStack>
</Box>
<Divider />



          {/* Medical Necessity Analysis */}
          {caseDetails.full_results?.analysis?.medical_necessity_analysis && (
            <Box>
              <Heading size="md" mb={2}>Medical Necessity Analysis</Heading>
              <Text fontWeight="bold">Overall Assessment:</Text>
              <Text mb={2}>{caseDetails.full_results.analysis.medical_necessity_analysis.overall_assessment}</Text>
              
              {caseDetails.full_results.analysis.medical_necessity_analysis.key_findings && (
                <>
                  <Text fontWeight="bold">Key Findings:</Text>
                  <Text mb={2}>{caseDetails.full_results.analysis.medical_necessity_analysis.key_findings}</Text>
                </>
              )}
            </Box>
          )}
          
          <Divider />
          
          {/* Missing Diagnosis Suggestions */}
          {caseDetails.full_results?.missing_diagnosis_suggestions?.suggestions && 
           caseDetails.full_results.missing_diagnosis_suggestions.suggestions.length > 0 && (
            <Box>
              <Heading size="md" mb={2}>Missing Diagnosis Suggestions</Heading>
              <List spacing={2}>
                {caseDetails.full_results.missing_diagnosis_suggestions.suggestions.slice(0, 3).map((suggestion, index) => (
                  <ListItem key={index}>
                    <HStack alignItems="flex-start">
                      <ListIcon as={WarningIcon} color="orange.500" mt={1} />
                      <Box>
                        <Text fontWeight="bold">{suggestion.code} - {suggestion.description}</Text>
                        <Text fontSize="sm">{suggestion.relevance_to_ionm}</Text>
                      </Box>
                    </HStack>
                  </ListItem>
                ))}
                {caseDetails.full_results.missing_diagnosis_suggestions.suggestions.length > 3 && (
                  <Text fontStyle="italic" mt={1}>
                    + {caseDetails.full_results.missing_diagnosis_suggestions.suggestions.length - 3} more suggestions...
                  </Text>
                )}
              </List>
            </Box>
          )}
          
          <Divider />
          
          {/* Modality Support */}
          {caseDetails.full_results?.analysis?.modality_support && (
            <Box>
              <Heading size="md" mb={2}>Modality Support</Heading>
              <SimpleGrid columns={[1, null, 2]} spacing={3}>
                {Object.entries(caseDetails.full_results.analysis.modality_support).map(([modality, details]) => (
                  <Box key={modality} p={3} borderWidth="1px" borderRadius="md" 
                       bg={details.support_level?.toLowerCase().includes('well') ? "green.50" : "yellow.50"}>
                    <Text fontWeight="bold">{modality}</Text>
                    <Text>Support: {details.support_level}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}
          
          <Divider />
          
          {/* CPT Codes Summary */}
          <Box>
            <Heading size="md" mb={2}>CPT Codes</Heading>
            <SimpleGrid columns={[1, null, 2]} spacing={3}>
              {caseDetails.full_results?.cpt_codes_data?.pro && 
               Object.entries(caseDetails.full_results.cpt_codes_data.pro).map(([key, code]) => (
                <Box key={key} p={3} borderWidth="1px" borderRadius="md">
                  <Text fontWeight="bold">{code.cpt_code}</Text>
                  <Text fontSize="sm" noOfLines={2}>{code.cpt_description}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </VStack>
      ) : (
        <Alert status="error">
          <AlertIcon />
          Failed to load case details
        </Alert>
      )}
    </ModalBody>
    <ModalFooter>
      <Button 
        as={RouterLink}
        to={`/ionm-coding-optimizer?case_id=${selectedCase?.case_id}`}
        colorScheme="blue" 
        mr={3}
      >
        Full Review
      </Button>
      <Button onClick={() => setIsModalOpen(false)}>Close</Button>
    </ModalFooter>
  </ModalContent>
</Modal>


    </Box>
  );
};

export default IONMReview;